import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import {
  Button,
  Form,
  Input,
  Label,
  Select,
  Textarea,
  ErrorText,
} from "../../../styledComponent";
import styles from "./style.module.scss";
import { dealerApplicationInitialValues } from "../../../../utils/formik/initialvalue";
import { dealerFormValidationSchema } from "../../../../utils/formik/validationSchema";
import {
  normalizePhoneInput,
  countWords,
  countCharacters,
} from "../../../../utils/helpers";
import { BASE_URL, POST_API_URLS } from "../../../../utils/apiConstant";
import axios from "axios";
import { toast } from "react-toastify";
// import { Checkbox } from "../../../ui";

const BecomeDealerForm = (props) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const formikRef = useRef(null);
  const formik = useFormik({
    initialValues: dealerApplicationInitialValues,
    validationSchema: dealerFormValidationSchema,
    onSubmit: (values) => formSubmitHandler(values),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      formikRef.current = formik;
      formikRef.current.setFieldValue("phone", phoneNumber);
    }, 1000);

    return () => clearTimeout(timer);
  }, [phoneNumber, formik]);

  // Handle checkbox array changes
  const handleCheckboxChange = (fieldName, value) => {
    const currentValues = formik.values[fieldName] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];
    formik.setFieldValue(fieldName, newValues);
  };

  // Transform form data to match API payload structure
  const transformFormDataToAPI = (formData) => {
    const ownsOperates = formData.option_applies?.includes("own_operate_gas_station") || false;
    const ownsProperty = formData.option_applies?.includes("own_property") || false;
    const hasCStore = formData.on_site_businesses?.includes("c_store") || false;
    const hasCarwash = formData.on_site_businesses?.includes("carwash") || false;
    const hasQSR = formData.on_site_businesses?.includes("qsr") || false;
    const hasOthers = formData.on_site_businesses?.includes("others") || false;
    const hasFSA = formData.fsa_in_place === "yes";
    const isZoned = formData.property_zoning === "yes";

    return {
      first_name: formData.first_name || "",
      last_name: formData.last_name || "",
      email: formData.email || "",
      phone: formData.phone || "",
      own_and_operate: ownsOperates,
      own_and_operate_brand: ownsOperates ? (formData.gas_station_brand || "") : "",
      own_property: ownsProperty,
      own_property_answer: ownsProperty ? (formData.property_details || "") : "",
      property_details: formData.property_info || "",
      total_lot_area_sqft: formData.lot_area_frontage || "",
      c_store: hasCStore,
      carwash: hasCarwash,
      qsr_brand: hasQSR ? (formData.qsr_brand || "") : "",
      others_business_flag: hasOthers,
      others_business_text: hasOthers ? (formData.others_specify || "") : "",
      has_fsa: ownsOperates ? hasFSA : false,
      fsa_expiry: (ownsOperates && hasFSA) ? (formData.fsa_expiry_date || "") : "",
      zoned_for_gas_station: ownsProperty ? isZoned : false,
    };
  };

  async function formSubmitHandler(data) {
    props.loader(true);

    try {
      // Transform form data to match API payload structure
      const apiPayload = transformFormDataToAPI(data);
      
      const apiResponse = await axios.post(
        `${BASE_URL + POST_API_URLS.DEALER_FORM}`,
        apiPayload
      );
      props.loader(false);
      apiResponse && props.success(true);
    } catch (error) {
      console.log("error", error?.response);
      props.loader(false);
      toast.error(
        error?.response?.data?.message || "Error while submitting Form",
        {
          position: "top-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
          style: { fontSize: "1.6rem", fontWeight: 500 },
        }
      );
    }
  }

  const ownsOperatesGasStation = formik.values.option_applies?.includes(
    "own_operate_gas_station"
  );
  const ownsProperty = formik.values.option_applies?.includes("own_property");
  const hasQSR = formik.values.on_site_businesses?.includes("qsr");
  const hasOthers = formik.values.on_site_businesses?.includes("others");
  const fsaYes = formik.values.fsa_in_place === "yes";

  return (
    <Form onSubmit={formik.handleSubmit} className={styles.becomeDealerForm}>
      {/* Left Column */}
      <div className={styles.leftColumn}>
        {/* First Name and Last Name */}
        <div className={styles.nameRow}>
          <div>
            <Label htmlFor="first_name">First Name*</Label>
            <Input
              type="text"
              id="first_name"
              maxLength={15}
              {...formik.getFieldProps("first_name")}
              style={
                formik.touched.first_name &&
                formik.errors.first_name && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            {formik.touched.first_name && formik.errors.first_name && (
              <ErrorText className={styles.errorText}>
                {formik.errors.first_name}
              </ErrorText>
            )}
          </div>
        </div>
        <div>
          <div>
            <Label htmlFor="last_name">Last Name*</Label>
            <Input
              type="text"
              id="last_name"
              maxLength={15}
              {...formik.getFieldProps("last_name")}
              style={
                formik.touched.last_name &&
                formik.errors.last_name && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            {formik.touched.last_name && formik.errors.last_name && (
              <ErrorText className={styles.errorText}>
                {formik.errors.last_name}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <div>
            <Label htmlFor="email">Email*</Label>
            <Input
              type="email"
              id="email"
              {...formik.getFieldProps("email")}
              style={
                formik.touched.email &&
                formik.errors.email && { boxShadow: "0px 0px 0px 2px red" }
              }
            />
            {formik.touched.email && formik.errors.email && (
              <ErrorText className={styles.errorText}>
                {formik.errors.email}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Phone Number */}
        <div>
          <div>
            <Label htmlFor="phone">Phone Number*</Label>
            <Input
              type="text"
              name="phone"
              id="phone"
              placeholder="xxx-xxx-xxxx"
              maxLength={12}
              onBlur={formik.handleBlur("phone")}
              onChange={(e) => {
                // Only allow digits and dashes
                const input = e.target.value.replace(/[^\d-]/g, "");
                setPhoneNumber((prev) => normalizePhoneInput(input, prev));
              }}
              value={phoneNumber}
              style={
                formik.touched.phone &&
                formik.errors.phone && { boxShadow: "0px 0px 0px 2px red" }
              }
            />
            {formik.touched.phone && formik.errors.phone && (
              <ErrorText className={styles.errorText}>
                {formik.errors.phone}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Please select the option that applies to you */}
        <div>
          <div>
            <Label>Please select the option that applies to you:*</Label>
            <div className={styles.checkboxGroup}>
              <label className="material-checkbox">
                <input
                  type="checkbox"
                  name="option_applies"
                  value="own_operate_gas_station"
                  checked={
                    formik.values.option_applies?.includes(
                      "own_operate_gas_station"
                    ) || false
                  }
                  onChange={() =>
                    handleCheckboxChange(
                      "option_applies",
                      "own_operate_gas_station"
                    )
                  }
                  onBlur={formik.handleBlur("option_applies")}
                />
                <span className="checkmark"></span>I own and operate a gas
                station
              </label>
              {ownsOperatesGasStation && (
                <div className={styles.conditionalField}>
                  <Label htmlFor="gas_station_brand">
                    Specify the brand (e.g., Esso). Max 5 words*
                  </Label>
                  <Input
                    type="text"
                    id="gas_station_brand"
                    {...formik.getFieldProps("gas_station_brand")}
                    style={
                      formik.touched.gas_station_brand &&
                      formik.errors.gas_station_brand && {
                        boxShadow: "0px 0px 0px 2px red",
                      }
                    }
                  />
                  <div className={styles.wordCount}>
                    Words: {countWords(formik.values.gas_station_brand)}/5
                  </div>
                  {formik.touched.gas_station_brand &&
                    formik.errors.gas_station_brand && (
                      <ErrorText className={styles.errorText}>
                        {formik.errors.gas_station_brand}
                      </ErrorText>
                    )}
                </div>
              )}

              <label className="material-checkbox">
                <input
                  type="checkbox"
                  name="option_applies"
                  value="own_property"
                  checked={
                    formik.values.option_applies?.includes("own_property") ||
                    false
                  }
                  onChange={() =>
                    handleCheckboxChange("option_applies", "own_property")
                  }
                  onBlur={formik.handleBlur("option_applies")}
                />
                <span className="checkmark"></span>I own a property
              </label>
              {ownsProperty && (
                <div className={styles.conditionalField}>
                  <Label htmlFor="property_details">
                    Provide details (Max 200 words)*
                  </Label>
                  <Textarea
                    id="property_details"
                    rows={6}
                    {...formik.getFieldProps("property_details")}
                    style={
                      formik.touched.property_details &&
                      formik.errors.property_details && {
                        boxShadow: "0px 0px 0px 2px red",
                      }
                    }
                  />
                  <div className={styles.wordCount}>
                    Words: {countWords(formik.values.property_details)}/200
                  </div>
                  {formik.touched.property_details &&
                    formik.errors.property_details && (
                      <ErrorText className={styles.errorText}>
                        {formik.errors.property_details}
                      </ErrorText>
                    )}
                </div>
              )}
            </div>
            {formik.touched.option_applies && formik.errors.option_applies && (
              <ErrorText className={styles.errorText}>
                {formik.errors.option_applies}
              </ErrorText>
            )}
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className={styles.rightColumn}>
        {/* Property Information */}
        <div>
          <div>
            <Label htmlFor="property_info">
              Please provide the information about the gas station or property
              you own.*
            </Label>
            <Textarea
              id="property_info"
              rows={6}
              placeholder="Provide details of property or gas station. (Insert address.)"
              {...formik.getFieldProps("property_info")}
              style={
                formik.touched.property_info &&
                formik.errors.property_info && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            <div className={styles.wordCount}>
              Words: {countWords(formik.values.property_info)}/200
            </div>
            {formik.touched.property_info && formik.errors.property_info && (
              <ErrorText className={styles.errorText}>
                {formik.errors.property_info}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Lot Area and Frontage */}
        <div>
          <div>
            <Label htmlFor="lot_area_frontage">
              What is the total lot area and frontage of the gas station or
              property you own? (in sq ft)*
            </Label>
            <Input
              type="text"
              id="lot_area_frontage"
              placeholder="Enter number only (sq ft)"
              {...formik.getFieldProps("lot_area_frontage")}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/[^\d]/g, "");
                formik.setFieldValue("lot_area_frontage", value);
              }}
              style={
                formik.touched.lot_area_frontage &&
                formik.errors.lot_area_frontage && {
                  boxShadow: "0px 0px 0px 2px red",
                }
              }
            />
            {formik.touched.lot_area_frontage &&
              formik.errors.lot_area_frontage && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.lot_area_frontage}
                </ErrorText>
              )}
          </div>
        </div>

        {/* On-site Businesses */}
        <div>
          <div>
            <Label>
              If you own a gas station, what other businesses are included
              on-site?*
            </Label>
            <div className={styles.checkboxGroup}>
              <label className="material-checkbox">
                <input
                  type="checkbox"
                  name="on_site_businesses"
                  value="c_store"
                  checked={
                    formik.values.on_site_businesses?.includes("c_store") ||
                    false
                  }
                  onChange={() =>
                    handleCheckboxChange("on_site_businesses", "c_store")
                  }
                  onBlur={formik.handleBlur("on_site_businesses")}
                />
                <span className="checkmark"></span>
                C-store
              </label>

              <label className="material-checkbox">
                <input
                  type="checkbox"
                  name="on_site_businesses"
                  value="carwash"
                  checked={
                    formik.values.on_site_businesses?.includes("carwash") ||
                    false
                  }
                  onChange={() =>
                    handleCheckboxChange("on_site_businesses", "carwash")
                  }
                  onBlur={formik.handleBlur("on_site_businesses")}
                />
                <span className="checkmark"></span>
                Carwash
              </label>

              <label className="material-checkbox">
                <input
                  type="checkbox"
                  name="on_site_businesses"
                  value="qsr"
                  checked={
                    formik.values.on_site_businesses?.includes("qsr") || false
                  }
                  onChange={() =>
                    handleCheckboxChange("on_site_businesses", "qsr")
                  }
                  onBlur={formik.handleBlur("on_site_businesses")}
                />
                <span className="checkmark"></span>
                QSR (add brand)
              </label>
              {hasQSR && (
                <div className={styles.conditionalField}>
                  <Label htmlFor="qsr_brand">QSR Brand*</Label>
                  <Input
                    type="text"
                    id="qsr_brand"
                    {...formik.getFieldProps("qsr_brand")}
                    style={
                      formik.touched.qsr_brand &&
                      formik.errors.qsr_brand && {
                        boxShadow: "0px 0px 0px 2px red",
                      }
                    }
                  />
                  {formik.touched.qsr_brand && formik.errors.qsr_brand && (
                    <ErrorText className={styles.errorText}>
                      {formik.errors.qsr_brand}
                    </ErrorText>
                  )}
                </div>
              )}

              <label className="material-checkbox">
                <input
                  type="checkbox"
                  name="on_site_businesses"
                  value="others"
                  checked={
                    formik.values.on_site_businesses?.includes("others") ||
                    false
                  }
                  onChange={() =>
                    handleCheckboxChange("on_site_businesses", "others")
                  }
                  onBlur={formik.handleBlur("on_site_businesses")}
                />
                <span className="checkmark"></span>
                Others (Please specify)
              </label>
              {hasOthers && (
                <div className={styles.conditionalField}>
                  <Label htmlFor="others_specify">Please specify*</Label>
                  <Input
                    type="text"
                    id="others_specify"
                    maxLength={200}
                    {...formik.getFieldProps("others_specify")}
                    style={
                      formik.touched.others_specify &&
                      formik.errors.others_specify && {
                        boxShadow: "0px 0px 0px 2px red",
                      }
                    }
                  />
                  <div className={styles.charCount}>
                    Characters: {countCharacters(formik.values.others_specify)}
                    /200
                  </div>
                  {formik.touched.others_specify &&
                    formik.errors.others_specify && (
                      <ErrorText className={styles.errorText}>
                        {formik.errors.others_specify}
                      </ErrorText>
                    )}
                </div>
              )}
            </div>
            {formik.touched.on_site_businesses &&
              formik.errors.on_site_businesses && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.on_site_businesses}
                </ErrorText>
              )}
          </div>
        </div>

        {/* FSA Question - Only show if owns/operates gas station */}
        {ownsOperatesGasStation && (
          <div>
            <div>
              <Label htmlFor="fsa_in_place">
                If you own and operate a gas station, do you currently have a
                Fuel Supply Agreement (FSA) in place?*
              </Label>
              <Select
                id="fsa_in_place"
                {...formik.getFieldProps("fsa_in_place")}
                style={
                  formik.touched.fsa_in_place &&
                  formik.errors.fsa_in_place && {
                    boxShadow: "0px 0px 0px 2px red",
                  }
                }
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Select>
              {formik.touched.fsa_in_place && formik.errors.fsa_in_place && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.fsa_in_place}
                </ErrorText>
              )}
            </div>
            {fsaYes && (
              <div>
                <div>
                  <Label htmlFor="fsa_expiry_date">
                    When does the FSA expire?*
                  </Label>
                  <Input
                    type="date"
                    id="fsa_expiry_date"
                    {...formik.getFieldProps("fsa_expiry_date")}
                    style={
                      formik.touched.fsa_expiry_date &&
                      formik.errors.fsa_expiry_date && {
                        boxShadow: "0px 0px 0px 2px red",
                      }
                    }
                  />
                  {formik.touched.fsa_expiry_date &&
                    formik.errors.fsa_expiry_date && (
                      <ErrorText className={styles.errorText}>
                        {formik.errors.fsa_expiry_date}
                      </ErrorText>
                    )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Property Zoning - Only show if owns property */}
        {ownsProperty && (
          <div>
            <div>
              <Label htmlFor="property_zoning">
                If you own a property, is it zoned for a gas station?*
              </Label>
              <Select
                id="property_zoning"
                {...formik.getFieldProps("property_zoning")}
                style={
                  formik.touched.property_zoning &&
                  formik.errors.property_zoning && {
                    boxShadow: "0px 0px 0px 2px red",
                  }
                }
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Select>
              {formik.touched.property_zoning &&
                formik.errors.property_zoning && (
                  <ErrorText className={styles.errorText}>
                    {formik.errors.property_zoning}
                  </ErrorText>
                )}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button - Spans both columns */}
      <Button
        $width="100%"
        type="submit"
        className={
          !(formik.isValid && formik.dirty) ? "disabledBtn" : "submitBtn"
        }
      >
        Submit
      </Button>
    </Form>
  );
};

export default BecomeDealerForm;
